import React, { createContext, useCallback, useContext } from 'react'
import { FeedContext } from 'src/feed/feedContext'
import useListenToLatestCoachMessages from 'src/feed/useListenToLatestCoachMessages'
import useMarkFeedAsSeen from 'src/feed/useMarkFeedAsSeen'
import { usePatientConversation } from '../../api/patientConversation'
import {
  MarkPatientFeedSeenDocument,
  useMarkPatientFeedUnreadFromMutation,
} from '../../api/patientConversation.generated'
import { toIso } from '../../format'
import { useHideSharedChat } from './useHideSharedChat'

// `undefined` while the marker loads, `null` when there is none — also the value outside a provider.
const LastSeenAtContext = createContext<string | null | undefined>(null)

export const useLastSeenAt = () => useContext(LastSeenAtContext)

type Props = { uid: string; markSeenEnabled: boolean; children: React.ReactNode }

// What Standard's `ParticipantFeedCombined` does once per feed, shared by every view of the conversation.
const PatientFeedProvider = ({ uid, markSeenEnabled, children }: Props) => {
  const hideSharedChat = useHideSharedChat()
  const { items, lastSeenAt, setLastSeenAt, fetchMoreNewer, isReadyToMarkSeen } = usePatientConversation(uid, {
    hideSharedChat,
  })
  const [markPatientFeedUnreadFrom] = useMarkPatientFeedUnreadFromMutation()

  useListenToLatestCoachMessages(uid, fetchMoreNewer)

  const { withAutoMarkSeenPaused } = useMarkFeedAsSeen(
    uid,
    items,
    markSeenEnabled && isReadyToMarkSeen && lastSeenAt !== undefined,
    MarkPatientFeedSeenDocument,
    { waitForParticipantList: false } // The standalone Advanced caseload does not publish Standard's analytics snapshot.
  )

  const markAsUnread = useCallback(
    (createdAt: Date) =>
      withAutoMarkSeenPaused(async () => {
        const { data } = await markPatientFeedUnreadFrom({ variables: { uid, from: createdAt } })
        const seenAt = toIso(data?.markFeedUnreadFrom.seenAt)
        if (!seenAt) {
          return false
        }
        setLastSeenAt(seenAt)
        return true
      }),
    [markPatientFeedUnreadFrom, setLastSeenAt, uid, withAutoMarkSeenPaused]
  )

  return (
    <FeedContext.Provider value={{ fetchMoreNewer, markAsUnread }}>
      <LastSeenAtContext.Provider value={lastSeenAt}>{children}</LastSeenAtContext.Provider>
    </FeedContext.Provider>
  )
}

export default PatientFeedProvider
